// Shared searchable model picker used by:
//   • Workspace BYOK card (OpenRouter axis)
//   • Super Admin → AI Routing (both Lovable AI + OpenRouter axes)
//
// Props are storage-format-neutral: the picker reads/writes a plain
// "provider/model" string. A "Use custom model" row keeps the no-allowlist
// promise for OpenRouter and lets Lovable-axis power users override
// (with a warning).

import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useOpenRouterModelCatalog,
  OPENROUTER_LAB_ORDER,
  OPENROUTER_SAFETY_NET,
  formatContextLength,
  type OpenRouterModel,
} from "@/lib/openrouterModelCatalog";
import { LOVABLE_AI_MODELS, LOVABLE_AI_PROVIDER_ORDER } from "@/lib/lovableAiModelCatalog";

type CatalogKind = "openrouter" | "lovable";

export type ModelPickerComboboxProps = {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
  ariaLabel: string;
  catalog: CatalogKind;
  triggerClassName?: string;
};

type Row = { id: string; name: string; group: string; contextLength?: number };

export const ModelPickerCombobox = ({
  value,
  onChange,
  placeholder,
  disabled,
  ariaLabel,
  catalog,
  triggerClassName,
}: ModelPickerComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const orQuery = useOpenRouterModelCatalog(open && catalog === "openrouter");

  const { rows, groupOrder, isLoading, isError } = useMemo(() => {
    if (catalog === "lovable") {
      const rs: Row[] = LOVABLE_AI_MODELS.map((m) => ({
        id: m.id,
        name: m.name,
        group: m.provider,
        contextLength: m.contextLength,
      }));
      return { rows: rs, groupOrder: LOVABLE_AI_PROVIDER_ORDER as readonly string[], isLoading: false, isError: false };
    }
    const source: OpenRouterModel[] = orQuery.data ?? (orQuery.isError ? OPENROUTER_SAFETY_NET : []);
    const rs: Row[] = source.map((m) => ({
      id: m.id,
      name: m.name,
      group: m.lab,
      contextLength: m.contextLength,
    }));
    return { rows: rs, groupOrder: OPENROUTER_LAB_ORDER as readonly string[], isLoading: orQuery.isLoading, isError: orQuery.isError };
  }, [catalog, orQuery.data, orQuery.isError, orQuery.isLoading]);

  const grouped = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const g of groupOrder) map.set(g, []);
    for (const r of rows) {
      const bucket = map.get(r.group);
      if (bucket) bucket.push(r);
    }
    return map;
  }, [rows, groupOrder]);

  const triggerLabel = value?.trim()
    ? value
    : placeholder
      ? `${placeholder} (default)`
      : "Select model…";

  const handlePick = (v: string) => {
    onChange(v);
    setOpen(false);
    setSearch("");
  };

  const trimmedQuery = search.trim();
  const catalogHasQuery = trimmedQuery
    ? rows.some((r) => r.id.toLowerCase() === trimmedQuery.toLowerCase())
    : false;
  const showCustomRow = trimmedQuery.length > 0 && !catalogHasQuery;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value?.trim() && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[340px] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder={catalog === "openrouter" ? "Search OpenRouter models…" : "Search Lovable AI models…"}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[360px]">
            {catalog === "openrouter" && isLoading ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading catalog…
              </div>
            ) : null}

            {catalog === "openrouter" && isError ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                Couldn't reach OpenRouter — showing a small fallback list. You can still type any model ID.
              </div>
            ) : null}

            {groupOrder.map((group) => {
              const items = grouped.get(group) ?? [];
              if (items.length === 0) return null;
              return (
                <CommandGroup key={group} heading={`${group} (${items.length})`}>
                  {items.map((r) => {
                    const isSel = value === r.id;
                    const ctx = formatContextLength(r.contextLength);
                    return (
                      <CommandItem
                        key={r.id}
                        value={`${r.id} ${r.name} ${r.group}`}
                        onSelect={() => handlePick(r.id)}
                        className="flex items-center gap-2"
                      >
                        <Check className={cn("h-3.5 w-3.5 shrink-0", isSel ? "opacity-100" : "opacity-0")} />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm">{r.name}</span>
                          <span className="truncate text-[11px] text-muted-foreground">{r.id}</span>
                        </div>
                        {ctx ? (
                          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                            {ctx}
                          </span>
                        ) : null}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}

            {showCustomRow ? (
              <>
                <CommandSeparator />
                <CommandGroup heading="Custom">
                  <CommandItem
                    value={`__custom__ ${trimmedQuery}`}
                    onSelect={() => handlePick(trimmedQuery)}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span className="text-sm">
                      Use custom model: <span className="font-mono">{trimmedQuery}</span>
                    </span>
                    {catalog === "lovable" ? (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400">
                        Must match a Lovable AI Gateway allowlist ID, or requests will 400.
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">
                        Saved verbatim as an OpenRouter model ID.
                      </span>
                    )}
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}

            {!isLoading && rows.length === 0 && !showCustomRow ? (
              <CommandEmpty>No models available.</CommandEmpty>
            ) : null}

            {value?.trim() ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__clear__"
                    onSelect={() => handlePick("")}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                    <span>Clear — use platform default</span>
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
